import {
  ConsoleOutputLevel,
  HasValue,
  OutputToConsoleFunc,
  SessionMetadata,
} from "../@types"
import { ApplicationEvents } from "./applicationEvents"
import { TestObject3 } from "./config3"
import makeExecutor from "./makeExecutor"
import { Scheduler } from "./measurementScheduler"
import { PerformanceEntryManager } from "./openinsights/resourceTiming"
import { PageSettings } from "./pageSettings"
import {
  createPlatformTestObject,
  PlatformTestObject,
} from "./platformTestObject"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"
import { selectWeightedRandomSample } from "./selectRandomSample"
import { SessionConfig } from "./sessionConfig"

export interface Dependencies {
  applicationEvents: ApplicationEvents
  callClearTimeout: (depID: number, timeoutID: number) => void
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearResourceTimings: () => void
  maxTargetFrequency: HasValue<number>
  measurementScheduler: Scheduler
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

// A cold-start platform is measured over a connection warmed with the small
// warm-up object; without that path the only alternatives corrupt the sample
// (re-downloading the large object skews the timing, skipping the warm-up
// measures a cold connection), so a cold-start platform missing its warm-up
// path is malformed config and is excluded rather than measured. Applied to
// every population that can reach makeExecutor -- the scheduled measurement
// population (buildPopulation) and the sentinel-detection population
// (buildPreliminaryPopulation) -- so the invariant "cold-start enabled =>
// warm-up path present" holds on every path to a measurement. This is a
// configuration concern resolved once at population build, not a
// per-measurement runtime condition.
export const filterMisconfiguredColdStartPlatforms = (
  platforms: PlatformTestObject[],
  outputToConsole: OutputToConsoleFunc,
) =>
  platforms.filter((platform) => {
    if (platform.coldStartDownloadEnabled && !platform.coldStartWarmupPath) {
      outputToConsole(
        ConsoleOutputLevel.warn,
        () =>
          `Excluding cold-start platform ${platform.dopplerPlatformID}:` +
          ` config enables cold start but supplies no warm-up path`,
      )
      return false
    }
    return true
  })

const buildPopulation = (
  objectsSource: TestObject3[],
  endpointDoppler: string,
  endpointPulsarBase: string,
  outputToConsole: OutputToConsoleFunc,
) =>
  filterMisconfiguredColdStartPlatforms(
    objectsSource.map((sourceObj) =>
      createPlatformTestObject(sourceObj, endpointDoppler, endpointPulsarBase),
    ),
    outputToConsole,
  )

// The sentinel-detection population. Reuses buildPopulation so the cold-start
// misconfiguration filter and any future population-build invariant apply
// identically to sentinel probes, which also execute through makeExecutor.
export const buildPreliminaryPopulation = (
  objectsSource: TestObject3[],
  sessionConfig: SessionConfig,
  outputToConsole: OutputToConsoleFunc,
): PlatformTestObject[] =>
  buildPopulation(
    objectsSource,
    sessionConfig.beaconEndpointInfo.dopplerEndpoints
      .dopplerResourceTimingEndpoint,
    sessionConfig.beaconEndpointInfo.pulsarEndpointBase,
    outputToConsole,
  )

export const initializeScheduledPopulation = async (
  dependencies: Dependencies,
  sessionMetadata: SessionMetadata,
  pageSettings: PageSettings,
  objectsSource: TestObject3[],
  sessionConfig: SessionConfig,
  sentinelsUsed: PlatformTestObject[] = [],
) => {
  const population = buildPopulation(
    objectsSource,
    sessionConfig.beaconEndpointInfo.dopplerEndpoints
      .dopplerResourceTimingEndpoint,
    sessionConfig.beaconEndpointInfo.pulsarEndpointBase,
    dependencies.outputToConsole,
  )

  // Create a set for fast lookup of used sentinels
  const sentinelPlatformIds = new Set(
    sentinelsUsed.map(
      (platform) => `${platform.dopplerPlatformID}-${platform.testObjectSize}`,
    ),
  )

  // Filter out used sentinels from immediate measurement candidates
  const availableForImmediateMeasurement = population.filter(
    (platform) =>
      !sentinelPlatformIds.has(
        `${platform.dopplerPlatformID}-${platform.testObjectSize}`,
      ),
  )

  // Adjust immediate measurement count to account for sentinel usage
  const maxInitialTestObjects =
    sessionConfig.siteOwnerSettings.maxInitialTestObjects
  const sentinelCount = sentinelsUsed.length
  const adjustedImmediateCount = Math.max(
    0,
    maxInitialTestObjects - sentinelCount,
  )

  // Add diagnostic message here
  dependencies.outputToConsole(
    ConsoleOutputLevel.debug,
    () =>
      `Max initial measurements: ${maxInitialTestObjects} (configured), ` +
      `${adjustedImmediateCount} (adjusted for ${sentinelCount} sentinels)`,
  )
  dependencies.outputToConsole(
    ConsoleOutputLevel.debug,
    () =>
      `Creating ${adjustedImmediateCount} regular measurements. ` +
      `Dormancy state: ${
        dependencies.runtimeValues.trackerBlockingDetected
          ? "BLOCKED"
          : "ACTIVE"
      }`,
  )

  // Draw a weighted random sample for immediate measurement
  const sampleToMeasureImmediately = selectWeightedRandomSample(
    availableForImmediateMeasurement,
    adjustedImmediateCount,
    dependencies.newRandomNumber,
    // Use the target frequency as the "weighting factor". Since this could
    // be a decimal number less than 1, be sure to round up
    (config) => Math.ceil(config.targetFrequency),
    // Expect target frequencies less than 1 per minute
    60,
  )

  // Pass the immediate measurement sample as skip targets for scheduler initialization.
  // Sentinels were already measured during detection and will get normal scheduling
  // delays; the immediate sample will be explicitly skipped since they execute next.
  const allImmediateMeasurements = [...sampleToMeasureImmediately]

  dependencies.measurementScheduler.initializePopulation(
    population,
    sessionMetadata,
    allImmediateMeasurements,
  )

  // Convert only the immediate measurements to "executables" for OpenInsights to process.
  // Sentinels were already executed during the detection phase (in performSentinelDetection
  // in client.ts) and do not need to run again immediately here, but they remain in the
  // scheduler population for future measurement cycles.
  return sampleToMeasureImmediately.map((config) =>
    makeExecutor(
      {
        applicationEvents: dependencies.applicationEvents,
        callClearTimeout: dependencies.callClearTimeout,
        callSendBeacon: dependencies.callSendBeacon,
        callSetTimeout: dependencies.callSetTimeout,
        callClearResourceTimings: dependencies.callClearResourceTimings,
        callFetch: dependencies.callFetch,
        maxTargetFrequency: dependencies.maxTargetFrequency,
        measurementScheduler: dependencies.measurementScheduler,
        newAbortController: dependencies.newAbortController,
        newDate: dependencies.newDate,
        newEpochTimestamp: dependencies.newEpochTimestamp,
        newMonotonicTimestamp: dependencies.newMonotonicTimestamp,
        newRandomNumber: dependencies.newRandomNumber,
        outputToConsole: dependencies.outputToConsole,
        performanceEntryAwaitTimeouts:
          dependencies.performanceEntryAwaitTimeouts,
        performanceEntryManager: dependencies.performanceEntryManager,
        runtimeValues: dependencies.runtimeValues,
      },
      config,
      sessionMetadata,
      false,
    ),
  )
}

export const updateScheduledPopulation = (
  measurementScheduler: Scheduler,
  sessionMetadata: SessionMetadata,
  objectsSource: TestObject3[],
  endpointDoppler: string,
  endpointPulsarBase: string,
  outputToConsole: OutputToConsoleFunc,
) =>
  measurementScheduler.updatePopulation(
    buildPopulation(
      objectsSource,
      endpointDoppler,
      endpointPulsarBase,
      outputToConsole,
    ),
    sessionMetadata,
  )
